import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faTrash, faLeaf, faXmark, faSpinner, faMapLocationDot } from '@fortawesome/free-solid-svg-icons';
import { getFloorPlan, getFloorPlanImageUrl, uploadFloorPlan, deleteFloorPlan } from '../utils/api';
import { useToast } from '../context/ToastContext';
import FloorPlanUploadModal from './FloorPlanUploadModal';
import ConfirmationModal from './ConfirmationModal';

// MIME type carrying a device id in HTML5 drags from the device list, cards
// and the unplaced strip onto the plan.
export const DEVICE_DRAG_TYPE = 'application/x-cleverplant-device';

// A pointer that moves less than this before release is a click, not a drag.
const CLICK_SLOP_PX = 4;

const clamp01 = (v) => Math.min(1, Math.max(0, v));

const isPlaced = (device) => device.plan_x != null && device.plan_y != null;

const DeviceAvatar = ({ device, className }) => (
        device.image
                ? <img src={`/api/v2/devices/${device.id}/image`} alt="" draggable={false} className={`${className} object-cover`} />
                : <span className={`${className} flex items-center justify-center bg-brand/10 text-brand`}><FontAwesomeIcon icon={faLeaf} /></span>
);

// FloorPlan shows a room's floor plan image with its devices placed on it as
// markers. Positions are fractions (0-1) of the image width and height, so they
// hold at any display size. highlight is {deviceId} and is re-run whenever a
// new object is passed.
const FloorPlan = ({ room, devices, highlight, onPositionChange, onPlanRemoved }) => {
        const navigate = useNavigate();
        const { showToast } = useToast();
        const [plan, setPlan] = useState(null);
        const [status, setStatus] = useState('loading'); // loading | none | ready | error
        const [showUpload, setShowUpload] = useState(false);
        const [confirmRemove, setConfirmRemove] = useState(false);
        const [isDropTarget, setIsDropTarget] = useState(false);
        const [drag, setDrag] = useState(null); // {deviceId, x, y, startX, startY, moved}
        const [pulseId, setPulseId] = useState(null);
        const areaRef = useRef(null);
        const markerRefs = useRef({});

        const loadPlan = useCallback(async () => {
                setStatus('loading');
                try {
                        const res = await getFloorPlan(room.id);
                        if (res.status === 404) {
                                setPlan(null);
                                setStatus('none');
                                return;
                        }
                        if (!res.ok) throw new Error(`status ${res.status}`);
                        const { data } = await res.json();
                        setPlan(data);
                        setStatus('ready');
                } catch (e) {
                        console.error('Failed to load floor plan', e);
                        setStatus('error');
                }
        }, [room.id]);

        useEffect(() => {
                loadPlan();
        }, [loadPlan]);

        // Scroll to and pulse the highlighted device, on the plan or in the
        // unplaced strip.
        useEffect(() => {
                if (!highlight || status !== 'ready') return;
                const el = markerRefs.current[highlight.deviceId];
                el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setPulseId(highlight.deviceId);
                const t = setTimeout(() => setPulseId(null), 3000);
                return () => clearTimeout(t);
        }, [highlight, status]);

        const toFraction = (clientX, clientY) => {
                const rect = areaRef.current.getBoundingClientRect();
                return {
                        x: clamp01((clientX - rect.left) / rect.width),
                        y: clamp01((clientY - rect.top) / rect.height),
                };
        };

        const handleSave = async (blob) => {
                const formData = new FormData();
                formData.append('file', blob, 'floor-plan.png');
                try {
                        const res = await uploadFloorPlan(room.id, formData);
                        if (!res.ok) {
                                const body = await res.json().catch(() => ({}));
                                showToast('error', body.message || 'Could not save the floor plan.');
                                return false;
                        }
                        const { data } = await res.json();
                        setPlan(data);
                        setStatus('ready');
                        showToast('success', 'Floor plan saved.');
                        return true;
                } catch (e) {
                        showToast('error', 'Could not save the floor plan.');
                        return false;
                }
        };

        const handleRemove = async () => {
                setConfirmRemove(false);
                try {
                        const res = await deleteFloorPlan(room.id);
                        if (!res.ok) throw new Error(`status ${res.status}`);
                        setPlan(null);
                        setStatus('none');
                        onPlanRemoved();
                        showToast('success', 'Floor plan removed.');
                } catch (e) {
                        showToast('error', 'Could not remove the floor plan.');
                }
        };

        // HTML5 drops from the device list, cards and unplaced strip.
        const onDragOver = (e) => {
                if (!e.dataTransfer.types.includes(DEVICE_DRAG_TYPE)) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                setIsDropTarget(true);
        };

        const onDrop = (e) => {
                setIsDropTarget(false);
                const deviceId = parseInt(e.dataTransfer.getData(DEVICE_DRAG_TYPE), 10);
                if (!deviceId || !devices.some(d => d.id === deviceId)) return;
                e.preventDefault();
                const { x, y } = toFraction(e.clientX, e.clientY);
                onPositionChange(deviceId, x, y);
        };

        // Pointer drags move markers already on the plan; they work with touch
        // too, where HTML5 drag-and-drop does not.
        const onMarkerPointerDown = (e, device) => {
                if (e.button !== 0) return;
                e.preventDefault();
                e.currentTarget.setPointerCapture(e.pointerId);
                setDrag({ deviceId: device.id, x: device.plan_x, y: device.plan_y, startX: e.clientX, startY: e.clientY, moved: false });
        };

        const onMarkerPointerMove = (e) => {
                if (!drag) return;
                const moved = drag.moved || Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY) > CLICK_SLOP_PX;
                if (!moved) return;
                setDrag({ ...drag, ...toFraction(e.clientX, e.clientY), moved });
        };

        const onMarkerPointerUp = () => {
                if (!drag) return;
                const { deviceId, x, y, moved } = drag;
                setDrag(null);
                if (moved) {
                        onPositionChange(deviceId, x, y);
                } else {
                        navigate(`/device/${deviceId}`);
                }
        };

        if (status === 'loading') {
                return (
                        <div data-testid="floor-plan" className="mb-6 flex justify-center py-10 text-gray-400">
                                <FontAwesomeIcon icon={faSpinner} spin className="text-2xl" />
                        </div>
                );
        }

        if (status === 'error') {
                return (
                        <div data-testid="floor-plan" className="mb-6 rounded-2xl bg-white card-shadow p-6 text-center text-sm text-text-weak">
                                Could not load the floor plan.{' '}
                                <button onClick={loadPlan} className="text-brand font-medium hover:underline">Try again</button>
                        </div>
                );
        }

        const uploadModal = (
                <FloorPlanUploadModal isOpen={showUpload} onClose={() => setShowUpload(false)} onSave={handleSave} />
        );

        if (status === 'none') {
                return (
                        <div data-testid="floor-plan" className="mb-6">
                                <button
                                        onClick={() => setShowUpload(true)}
                                        data-testid="floor-plan-upload"
                                        className="w-full p-8 border-2 border-dashed border-gray-300 rounded-2xl text-center hover:border-brand hover:bg-gray-50 transition-colors"
                                >
                                        <FontAwesomeIcon icon={faMapLocationDot} className="text-gray-400 text-3xl mb-3" />
                                        <p className="text-gray-600 font-medium">Upload a floor plan for {room.name}</p>
                                        <p className="text-gray-400 text-sm mt-1">Then drag your plants onto it to show where they are.</p>
                                </button>
                                {uploadModal}
                        </div>
                );
        }

        const unplaced = devices.filter(d => !isPlaced(d));

        return (
                <div data-testid="floor-plan" className="mb-6 rounded-2xl bg-white card-shadow p-4 sm:p-6">
                        <div className="flex justify-end gap-2 mb-3">
                                <button
                                        onClick={() => setShowUpload(true)}
                                        data-testid="floor-plan-replace"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 rounded-md border border-gray-300 hover:bg-gray-50"
                                >
                                        <FontAwesomeIcon icon={faUpload} /> Replace
                                </button>
                                <button
                                        onClick={() => setConfirmRemove(true)}
                                        data-testid="floor-plan-remove"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 rounded-md border border-gray-300 hover:text-red-600 hover:border-red-300"
                                >
                                        <FontAwesomeIcon icon={faTrash} /> Remove
                                </button>
                        </div>

                        <div
                                ref={areaRef}
                                data-testid="floor-plan-area"
                                className={`relative select-none rounded-lg overflow-hidden border ${isDropTarget ? 'ring-2 ring-brand' : ''}`}
                                onDragOver={onDragOver}
                                onDragLeave={() => setIsDropTarget(false)}
                                onDrop={onDrop}
                        >
                                <img
                                        src={getFloorPlanImageUrl(room.id, plan.updated_at)}
                                        alt={`Floor plan of ${room.name}`}
                                        draggable={false}
                                        className="block w-full h-auto"
                                        style={{ aspectRatio: `${plan.width} / ${plan.height}` }}
                                />
                                {devices.filter(isPlaced).map(device => {
                                        const dragging = drag?.deviceId === device.id;
                                        const x = dragging ? drag.x : device.plan_x;
                                        const y = dragging ? drag.y : device.plan_y;
                                        const hasAlert = device.alerts?.length > 0;
                                        const pulsing = pulseId === device.id;
                                        return (
                                                <div
                                                        key={device.id}
                                                        ref={el => { markerRefs.current[device.id] = el; }}
                                                        data-testid="floor-plan-marker"
                                                        data-device-id={device.id}
                                                        className={`group absolute -translate-x-1/2 -translate-y-1/2 ${dragging ? 'z-20 cursor-grabbing' : 'z-10 cursor-grab'}`}
                                                        style={{ left: `${x * 100}%`, top: `${y * 100}%` }}
                                                >
                                                        {pulsing && <span className="absolute inset-0 rounded-full bg-brand/60 animate-ping" />}
                                                        <button
                                                                type="button"
                                                                title={device.name}
                                                                aria-label={`${device.name}, open details`}
                                                                className={`relative block w-9 h-9 touch-none rounded-full border-2 bg-white shadow-md overflow-hidden ${hasAlert ? 'border-red-500' : 'border-brand'} ${pulsing ? 'ring-4 ring-brand/40' : ''}`}
                                                                onPointerDown={(e) => onMarkerPointerDown(e, device)}
                                                                onPointerMove={onMarkerPointerMove}
                                                                onPointerUp={onMarkerPointerUp}
                                                                onPointerCancel={() => setDrag(null)}
                                                                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/device/${device.id}`); }}
                                                        >
                                                                <DeviceAvatar device={device} className="w-full h-full" />
                                                        </button>
                                                        <span className={`absolute left-1/2 top-full mt-1 -translate-x-1/2 whitespace-nowrap rounded bg-gray-900/80 px-1.5 py-0.5 text-xs text-white pointer-events-none ${pulsing || dragging ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                                                                {device.name}
                                                        </span>
                                                        <button
                                                                type="button"
                                                                data-testid="floor-plan-marker-remove"
                                                                title="Remove from floor plan"
                                                                aria-label={`Remove ${device.name} from floor plan`}
                                                                className="absolute -top-2 -right-2 hidden group-hover:flex w-5 h-5 items-center justify-center rounded-full bg-white border text-gray-500 hover:text-red-600 text-xs shadow"
                                                                onPointerDown={(e) => e.stopPropagation()}
                                                                onClick={() => onPositionChange(device.id, null, null)}
                                                        >
                                                                <FontAwesomeIcon icon={faXmark} />
                                                        </button>
                                                </div>
                                        );
                                })}
                        </div>

                        {unplaced.length > 0 && (
                                <div className="mt-4">
                                        <p className="text-sm text-text-weak mb-2">Not on the plan yet. Drag onto the plan to place:</p>
                                        <div className="flex flex-wrap gap-2">
                                                {unplaced.map(device => (
                                                        <div
                                                                key={device.id}
                                                                ref={el => { markerRefs.current[device.id] = el; }}
                                                                draggable
                                                                onDragStart={(e) => {
                                                                        e.dataTransfer.setData(DEVICE_DRAG_TYPE, String(device.id));
                                                                        e.dataTransfer.effectAllowed = 'move';
                                                                }}
                                                                data-testid="floor-plan-unplaced"
                                                                data-device-id={device.id}
                                                                className={`inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border bg-white text-sm cursor-grab hover:border-brand ${pulseId === device.id ? 'ring-2 ring-brand animate-pulse' : ''}`}
                                                        >
                                                                <DeviceAvatar device={device} className="w-6 h-6 rounded-full" />
                                                                {device.name}
                                                        </div>
                                                ))}
                                        </div>
                                </div>
                        )}

                        {uploadModal}
                        <ConfirmationModal
                                isOpen={confirmRemove}
                                onClose={() => setConfirmRemove(false)}
                                onConfirm={handleRemove}
                                title="Remove floor plan"
                                message={`Remove the floor plan for ${room.name}? All plants will be taken off it.`}
                        />
                </div>
        );
};

export default FloorPlan;
